import React, { useState, useEffect } from 'react';
import { Button, message, Tooltip } from 'antd';
import { EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getTimeLeft, getDetailText } from 'pages/dashboard/TextVisitPage/utils';
import { CaseListItem, ICaseStatus, TextVisitServiceType, IPaymentStatus } from 'types/textVisit';
import { releaseCase, clearUnreadMessage } from 'api/textVisit';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import MedicationRefillIcon from 'assets/conditionIcon/medication-refill.png';
import MedicationChangeIcon from 'assets/conditionIcon/medication-change.png';
import FollowUpConsultationIcon from 'assets/conditionIcon/follow-up.png';
import OtherCareNeedsIcon from 'assets/conditionIcon/others.png';
import DefaultIcon from 'assets/conditionIcon/default-condition.png';
import dayjs from 'utils/dayjs';
import { formatUTCTime } from 'utils/common';
import s from './s.module.less';

interface MyCaseCardProps {
    caseData: CaseListItem;
    onRefetch: () => void;
    onViewCase: (caseData: CaseListItem) => void;
    onResumeCase: (caseData: CaseListItem) => void;
}

export const FollowUpServiceTypeMap = {
    [TextVisitServiceType.MEDICATION_REFILL]: {
        title: 'Prescription refill',
        icon: MedicationRefillIcon,
    },
    [TextVisitServiceType.MEDICATION_CHANGE]: {
        title: 'Medication change',
        icon: MedicationChangeIcon,
    },
    [TextVisitServiceType.FOLLOW_UP_CONSULTATION]: {
        title: 'Follow-up consultation',
        icon: FollowUpConsultationIcon,
    },
    [TextVisitServiceType.OTHER]: {
        title: 'Other care needs',
        icon: OtherCareNeedsIcon,
    },
    [TextVisitServiceType.PRESCRIPTION_REFILL]: {
        title: 'Prescription refill',
        icon: MedicationRefillIcon,
    },
};

const MyCaseCard: React.FC<MyCaseCardProps> = ({ caseData, onRefetch, onViewCase, onResumeCase }) => {
    const [settings] = useSettings();
    const stateList = settings[EnumFields.STATE] || [];
    const [unreadCount, setUnreadCount] = useState(caseData.providerUnreadCount || 0);

    const releaseToOthers = async () => {
        try {
            const result = await releaseCase(caseData.id);
            if (!result.error) {
                onRefetch();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const isClosed = caseData.status === ICaseStatus.CLOSED;
    const isInProgress = caseData.status === ICaseStatus.ACTIVE && !caseData.providerTreatmentSubmittedAt;
    const canRelease = dayjs().diff(dayjs.utc(caseData.assignedProviderAt).local(), 'minutes') <= 10;
    const isFollowUpCase = caseData.serviceType !== TextVisitServiceType.INITIAL_VISIT;
    const isPrescriptionRefill = caseData.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL; // || caseData.serviceType === TextVisitServiceType.MEDICATION_REFILL;

    const handleViewCase = async () => {
        try {
            await clearUnreadMessage(caseData.id);
            setUnreadCount(0);
        } catch (error) {
            console.error(error);
        }
        onViewCase(caseData);
    };

    useEffect(() => {
        setUnreadCount(caseData.providerUnreadCount || 0);
    }, [caseData.providerUnreadCount]);

    return (
        <div className={s.myCaseCard}>
            <div className={s.caseInfo}>
                <div className={s.patientInfo}>
                    <h4 className={s.patientName}>
                        {caseData.patientFirstName} {caseData.patientLastName}
                    </h4>
                    <div className={s.caseLocation}>
                        <EnvironmentOutlined />
                        <span>{getDetailText(caseData, stateList)}</span>
                    </div>
                </div>
                {!isFollowUpCase ? (
                    <div className={s.caseIcon}>
                        <span className={caseData.icon ? s.caseIconImage : undefined}>
                            <img
                                src={caseData.icon || DefaultIcon}
                                alt={caseData.assessmentName}
                                width={24}
                                height={24}
                            />
                        </span>
                        <span>{caseData.assessmentName}</span>
                    </div>
                ) : (
                    <div className={s.caseIcon}>
                        <img
                            src={
                                FollowUpServiceTypeMap[caseData.serviceType as keyof typeof FollowUpServiceTypeMap]
                                    ?.icon
                            }
                            alt={
                                FollowUpServiceTypeMap[caseData.serviceType as keyof typeof FollowUpServiceTypeMap]
                                    ?.title
                            }
                            width={24}
                            height={24}
                        />
                        <span>
                            {FollowUpServiceTypeMap[caseData.serviceType as keyof typeof FollowUpServiceTypeMap]?.title}
                        </span>
                    </div>
                )}
                {!isClosed ? (
                    <div className={s.myCaseDetails}>
                        <div className={s.lastUpdated}>
                            Last updated: {formatUTCTime(caseData.updatedAt, 'MMM D, YYYY')}
                        </div>
                        {!isPrescriptionRefill && (
                            <div
                                className={s.timeLeft}
                                style={{ backgroundColor: getTimeLeft(caseData.assignedProviderAt || '').color }}
                            >
                                <ClockCircleOutlined />
                                <span>{getTimeLeft(caseData.assignedProviderAt || '').text}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={s.myCaseDetails} style={{ justifyContent: 'center' }}>
                        <div className={s.lastUpdated}>Closed on {formatUTCTime(caseData.closedAt, 'MMM D, YYYY')}</div>
                    </div>
                )}
            </div>
            <div className={s.caseActions}>
                {unreadCount > 0 && !isClosed && !isInProgress ? (
                    <div className={s.newMessageBadge} onClick={handleViewCase}>
                        <div className={s.redDot} />
                        <span>{unreadCount} new message</span>
                    </div>
                ) : null}
                {isInProgress &&
                    !isFollowUpCase &&
                    (canRelease ? (
                        <Button type="text" className={s.viewCasesBtn} onClick={releaseToOthers}>
                            Release to others
                        </Button>
                    ) : (
                        <Tooltip title="Cases can only be released within 10 minutes of accepting">
                            <Button type="text" className={s.viewCasesBtn} onClick={releaseToOthers} disabled>
                                Release to others
                            </Button>
                        </Tooltip>
                    ))}
                {isInProgress && (
                    <Button type="text" className={s.viewCasesBtn} onClick={() => onResumeCase(caseData)}>
                        Resume
                    </Button>
                )}
                {isClosed && caseData.paymentStatus === IPaymentStatus.SUCCEEDED && (
                    <div className={s.paidTag}>Paid</div>
                )}
                {isClosed && caseData.paymentStatus === IPaymentStatus.REFUNDED && (
                    <div className={s.refundedTag}>Refunded</div>
                )}
                {!isInProgress && (
                    <Button type="text" className={s.viewCasesBtn} onClick={handleViewCase}>
                        View cases
                    </Button>
                )}
            </div>
        </div>
    );
};

export default MyCaseCard;
